import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export type Language = 'es' | 'en' | 'ch';

interface LanguageContextType {
  language: Language;
  changeLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

const LANGUAGE_STORAGE_KEY = 'app_language';

const normalizeLanguage = (lang: string): Language => {
  const normalizedLang = lang.split('-')[0];
  if (normalizedLang === 'zh' || normalizedLang === 'ch') {
    return 'ch';
  }
  if (normalizedLang === 'es') {
    return 'es';
  }
  if (normalizedLang === 'en') {
    return 'en';
  }
  return 'ch'; // default
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n, t } = useTranslation();
  
  // Inicializar desde localStorage o desde i18n
  const [language, setLanguage] = useState<Language>(() => {
    try {
      const savedLang = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (savedLang && ['es', 'en', 'ch'].includes(savedLang)) {
        return savedLang as Language;
      }
    } catch (error) {
      console.warn('Failed to read language from localStorage', error);
    }
    
    // Fallback a i18n
    return normalizeLanguage(i18n.language);
  });

  // Sincronizar i18n con el idioma inicial del contexto
  useEffect(() => {
    const normalizedLang = normalizeLanguage(i18n.language);
    if (normalizedLang !== language) {
      i18n.changeLanguage(language);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo al montar

  // Sincronizar con cambios externos de i18n (pero solo si no fue iniciado por nosotros)
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      const normalizedLang = normalizeLanguage(lng);
      setLanguage((prevLang) => {
        // Solo actualizar si el idioma realmente cambió
        if (normalizedLang !== prevLang) {
          return normalizedLang;
        }
        return prevLang;
      });
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const changeLanguage = useCallback((lang: Language) => {
    // Actualizar el estado inmediatamente para que el UI se actualice
    setLanguage(lang);
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    } catch (error) {
      console.warn('Failed to save language to localStorage', error);
    }
    // Luego actualizar i18n (esto disparará el evento pero ya tenemos el estado actualizado)
    i18n.changeLanguage(lang).catch((err) => {
      console.warn('Failed to change language in i18n', err);
    });
  }, [i18n]);

  const value: LanguageContextType = useMemo(() => ({
    language,
    changeLanguage,
    t,
  }), [language, changeLanguage, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

